<template>
  <div class="c-config-content">
    <h4 class="c-config-content__title">{{ $t('userList') }}</h4>
    <div v-if="firstLoading">
      <skeleton-loading>
        <row>
          <square-skeleton :boxProperties="{bottom: '10px', height: '30px'}"></square-skeleton>
        </row>
        <row>
          <square-skeleton :boxProperties="{bottom: '10px', height: '200px'}"></square-skeleton>
        </row>
      </skeleton-loading>
    </div>
    <div v-else>
        <p v-if="this.$store.state.user.client.isTrial">
          {{ $t('youAreUsing') }}&nbsp;<span class="text-danger">{{ $t('trialVersion') }}</span>&nbsp;<b class="text-blue">{{ $t('upgradeToPaidVersion') }}</b>&nbsp;{{ $t('toHaveBetterExperience') }}
        </p>
        <p>{{ $t('listSummary', [firstItemOfPage, lastItemOfPage, itemCount, $t('users')]) }}</p>
        <div class="row is-slot-small mb-3">
          <div class="col-md-6 col-lg-3 mb-2 mb-lg-0">
            <b-form-select @change="search" v-model="filterFormModel.status" :options="activeOptions"
                           class="form-control custom-select"></b-form-select>
          </div>
          <div class="col-md-6 col-lg-3 mb-2 mb-lg-0">
            <user-group-select :model="filterFormModel" @change="search"></user-group-select>
          </div>
          <div class="col-md-6 col-lg-3 mb-2 mb-lg-0">
            <b-form-select :options="roles" v-model="filterFormModel.role" @change="search"
                           :errors="formErrors" class="form-group mr-md-2 flex-column"></b-form-select>
          </div>
          <div class="col-md-6 col-lg-3 mb-2 mb-lg-0">
            <div class="c-search-medium">
              <div class="c-search-medium__inner">
                <b-form-input class="form-control" v-model="filterFormModel.keyword"
                              :placeholder="$t('keyword')" @keyup="search">
                </b-form-input>
                <button @click="load" class="c-search-medium__btn" type="button">
                  <i class="icon16-search-gadient"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-template-table table-responsive">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>{{ $t('userName') }}</th>
              <th>{{ $t('email') }}</th>
              <th>{{ $t('ext') }}</th>
              <th class="text-right">
                <import-user></import-user>
                <span @click="create" class="btn-add" v-if="$store.state.user.permissions.includes('userManageAll')">
                  <i class="icon16-plus-circle-gadient" v-b-tooltip.hover.top="$t('create')"></i>
                </span>
              </th>
            </tr>
            </thead>
            <tbody v-if="listLoading">
            <tr>
              <td colspan="4" class="text-center"><b-spinner></b-spinner></td>
            </tr>
            </tbody>
            <tbody v-else-if="items && items.length">
            <tr v-for="item in items" :key="item._id">
              <td>
                <span :class="[(Date.now()-item.lastOnlineTime)<=60*15*1000?'c-signal is-success':'c-signal']">
                    <span class="h-word-break">{{ item.name }}</span>
                </span>
                <p>
                  <label class="badge badge-primary">{{getRoleName(item.role)}}</label>&nbsp;
                  <label class="badge badge-secondary">{{item.group ? item.group.name : $t('default')}}</label>
                </p>
              </td>
              <td><span class="h-word-break">{{ item.email }}</span></td>
              <td>{{ item.ext }}</td>
              <td class="text-right">
                <div v-if="$store.state.user.permissions.includes('userManageAll')">
                  <button @click="showFormReset(item)" class="btn btn-outline-gray btn-sm is-border-1 mr-1" type="button"
                          v-b-tooltip.hover.top="$t('resetPassword')" v-if="item.active">
                    <i class="fa fa-key mr-0"></i>
                  </button>
                  <button v-if="item.active" @click="updateActive(item, 'deactivate')" type="button"
                          v-b-tooltip.hover.top="$t('deactivate')" class="btn btn-outline-gray btn-sm is-border-1 mr-1">
                    <i class="fa fa-ban mr-0"></i>
                  </button>
                  <button @click="updateActive(item, 'activate')" v-b-tooltip.hover.top="$t('reactivate')"
                          class="btn btn-outline-gray btn-sm is-border-1 mr-1" type="button" v-if="!item.active">
                    <i class="icon12-refesh-primary"></i>
                  </button>
                  <button class="btn btn-outline-gray btn-sm is-border-1" :disabled="updateLoading"
                          type="button" v-b-tooltip.hover.top="$t('update')" @click="update(item._id)">
                    <b-spinner v-if="updateLoading" small></b-spinner>
                    <i v-if="!updateLoading" class="icon12-edit-primary"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
            <tbody v-else>
            <tr>
              <td class="text-center text-danger" colspan="4">{{ $t('emptyList') }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="c-paging" v-if="itemCount > filterFormModel.pageSize">
          <b-pagination size="sm" class="pagination pagination-sm justify-content-center"
                        v-model="filterFormModel.page" :total-rows="itemCount"
                        :per-page="filterFormModel.pageSize"
                        @input="load">
          </b-pagination>
        </div>
        <b-modal v-model="showForm" :title="formTitle + $t('userFormTitle')" @ok="save" no-close-on-backdrop no-enforce-focus>
          <form autocomplete="off">
            <div class="row">
              <div class="col-md">
                <form-input :label="$t('name')" :model="formModel" attribute="name" :errors="formErrors"
                            :placeholder="$t('userNamePlaceholder')">
                </form-input>
                <form-input :label="$t('email')" :model="formModel" attribute="email" :errors="formErrors"
                            type="email" :placeholder="$t('userEmailPlaceholder')"></form-input>
                <form-input v-if="!formModel._id" :label="$t('password')" :model="formModel"
                            attribute="password" :errors="formErrors" type="password" :placeholder="$t('userPasswordPlaceholder')"></form-input>
                <form-input :label="$t('phone')" :model="formModel" attribute="phone" :errors="formErrors"
                            :placeholder="$t('userPhonePlaceholder')">
                </form-input>
                <form-textarea :label="$t('desc')" :model="formModel" attribute="desc" :errors="formErrors"
                               :placeholder="$t('userDescPlaceholder')">
                </form-textarea>
                <recording-select :label="$t('moh')" :model="formModel" attribute="moh" :errors="formErrors"></recording-select>
              </div>
            </div>
            <div class="row" v-if="formModel._id">
              <div class="col-md">
                <form-input :label="$t('ext')" :model="formModel" attribute="ext"
                            :errors="formErrors" :placeholder="$t('extPlaceholder')"></form-input>
              </div>
              <div class="col-md">
                <form-input :label="$t('extPassword')" :model="formModel" attribute="extPassword"
                            :errors="formErrors" :placeholder="$t('extPasswordPlaceholder')"></form-input>
              </div>
            </div>
            <div class="row">
              <div class="col-md">
                <user-group-select-form :label="$t('group')" :model="formModel" :errors="formErrors"></user-group-select-form>
              </div>
              <div class="col-md">
                <form-select :label="$t('role')" :model="formModel" attribute="role" :errors="formErrors" :options="roles"></form-select>
              </div>
            </div>
          </form>
          <template v-slot:modal-footer>
            <b-button variant="secondary" class="float-right" @click="showForm = false">{{ $t('cancel') }}
            </b-button>
            <b-button :disabled="saveLoading" variant="primary" class="float-right" @click="save">
              <b-spinner v-if="saveLoading" small></b-spinner>
              {{ $t('save') }}
            </b-button>
          </template>
        </b-modal>
        <b-modal v-model="showDeactivateForm" :title="$t('deactivateUser')">
          <label>{{ $t('chooseAssignedUser', [activeFormModel.name]) }}</label>
          <user-select :model="activeFormModel" attribute="assignedUser" :errors="formErrors"></user-select>
          <template v-slot:modal-footer>
            <b-button variant="secondary" class="float-right" @click="showDeactivateForm = false">{{ $t('cancel') }}
            </b-button>
            <b-button :disabled="saveLoading" variant="primary" class="float-right" @click="saveActive('deactivate')">
              <b-spinner v-if="saveLoading" small></b-spinner>
              {{ $t('deactivate') }}
            </b-button>
          </template>
        </b-modal>
        <b-modal v-model="showActivateForm" :title="$t('activateUser')">
          <label>{{ $t('activateUserInfo', [activeFormModel.name, activeFormModel.email]) }}</label>
          <form-input :label="$t('ext')" :model="activeFormModel" attribute="ext"
                      :errors="formErrors" :placeholder="$t('extPlaceholder')"></form-input>
          <template v-slot:modal-footer>
            <b-button variant="secondary" class="float-right" @click="showActivateForm = false">{{ $t('cancel') }}
            </b-button>
            <b-button :disabled="saveLoading" variant="primary" class="float-right" @click="saveActive('activate')">
              <b-spinner v-if="saveLoading" small></b-spinner>
              {{ $t('activate') }}
            </b-button>
          </template>
        </b-modal>
        <b-modal v-model="showResetPasswordForm" :title="$t('resetPassword')" @ok="resetPassword" no-close-on-backdrop no-enforce-focus>
          <p v-if="resetPasswordModel.email"><span>{{$t('resetPasswordForAccount')}} </span>{{resetPasswordModel.email}}</p>
          <form-input :model="resetPasswordModel" attribute="password" :placeholder="$t('newPassword')"></form-input>
        </b-modal>
    </div>
  </div>
</template>

<script>
import list from "../../../mixins/List";
import crud from '../../../mixins/CRUD';
import UserGroupSelect from "../../../components/UserGroupSelect";
import UserGroupSelectForm from "../../../components/UserGroupSelectForm";
import _ from "lodash";
import UserSelect from "../../../components/UserSelect";
import RecordingSelect from "../../../components/RecordingSelect";
import ImportUser from "./ImportUser.vue";

export default {
  mixins: [list, crud],
  components: {UserSelect, UserGroupSelect, UserGroupSelectForm, RecordingSelect, ImportUser},
  props: ['roles'],
  data: function () {
    return {
      service: '/user',
      reloadBus: 'reloadUsers',
      activeOptions: [
        {value: null, text: this.$t('status')},
        {value: '0', text: this.$t('activated')},
        {value: '1', text: this.$t('deactivated')},
      ],
      defaultFilterFormModel: {
        status: '0',
        group: null,
        role: null
      },
      defaultFormModel: {
        active: true,
        role: 'agent',
        group: null
      },
      activeFormModel: {},
      showDeactivateForm: false,
      showActivateForm: false,
      showResetPasswordForm: false,
      resetPasswordModel: {
        password: null
      },
      resetPasswordLoading: false
    }
  },
  methods: {
    search: _.debounce(function () {
      this.formModel.page = 1;
      this.load();
    }, 200),
    updateActive(user, type) {
      this.activeFormModel.id = user._id;
      this.activeFormModel.name = user.name;
      if (type === 'deactivate') {
        this.showDeactivateForm = true;
      } else {
        this.showActivateForm = true;
        this.activeFormModel.ext = user.ext;
        this.activeFormModel.email = user.email;
      }
    },
    saveActive(type) {
      this.saveLoading = true;
      let uri;
      if (type === 'deactivate') {
        uri = this.service + '/deactivate/'
      } else {
        uri = this.service + '/activate/'
      }
      if (this.activeFormModel.id) {
        this.$service.put(uri + this.activeFormModel.id, this.activeFormModel).then(response => {
          if (response) {
            if (this.load) {
              this.load();
            }
            if (this.reloadBus) {
              this.$bus.$emit(this.reloadBus);
            }
            this.$swal({
              title: this.$t('success'),
              text: this.$t(type === 'deactivate' ? 'userDeactivated' : 'userActivated'),
              icon: 'success',
              toast: true,
              position: 'top-end',
              timer: 5000,
            });
            this.filterFormModel.active = 1;
            this.showDeactivateForm = false;
            this.showActivateForm = false;
          } else {
            this.$swal({
              title: this.$t('error'),
              text: this.$t('deactivatedFailed'),
              icon: 'error',
              toast: true,
              position: 'top-end',
              timer: 5000,
            });
          }
        }).catch(error => {
          if (error.response.data.message) {
            this.$swal({
              text: error.response.data.message,
              icon: 'error'
            });
          } else {
            this.formErrors = error.response.data;
          }
        }).finally(() => {
          this.saveLoading = false;
        })
      }
    },
    showFormReset(item){
      this.resetPasswordModel.id = item._id;
      this.resetPasswordModel.email = item.email;
      this.resetPasswordModel.password = null;
      this.showResetPasswordForm = true;
    },
    resetPassword: function () {
      this.resetPasswordLoading = true;
      this.$service.post(this.service + '/' + this.resetPasswordModel.id + '/password', this.resetPasswordModel).then(response => {
        if (response.data) {
          this.load();
          this.$swal({
            title: this.$t('success'),
            text: this.$t('resetSuccess'),
            icon: 'success'
          });
        } else {
          this.$swal({
            title: this.$t('error'),
            text: this.$t('resetFailed'),
            icon: 'error'
          });
        }
      }).finally(() => {
        this.resetPasswordLoading = false;
      });
    },
    getRoleName(role) {
      if (['owner', 'supervisor', 'agent'].includes(role)) {
        return this.$t(role)
      } else {
        let find = this.roles.find(r => r.value === role);
        if (find) {
          return find.text
        } else {
          return role
        }
      }
    }
  },
}
</script>